import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { pickBy } from '../../../util/common';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  IconArrowHead,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';
import { routeAndSubmitKeywordSearch } from '../../../util/searchSubmit';

import OnboardingModal from '../../OnboardingModal/OnboardingModal';

import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import ISOSearchBar from './ISOSearchBar/ISOSearchBar';
import SearchOverlayContent from './SearchOverlayContent/SearchOverlayContent';
import { HERO_SEARCH_PLACEHOLDER } from '../../HomePage/homeContent';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';
const MOBILE_MENU_BUTTON_ID = 'mobileMenuButton';
const MOBILE_SEARCH_BUTTON_ID = 'mobileSearchButton';

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

// ── Mobile-only sub-components ───────────────────────────────────────────────

const HamburgerIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="1" y1="5" x2="21" y2="5" />
    <line x1="1" y1="11" x2="21" y2="11" />
    <line x1="1" y1="17" x2="21" y2="17" />
  </svg>
);

const MagnifyingGlassIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" fill="none" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="5" y1="5" x2="19" y2="19" stroke="#3a0104" strokeWidth="2" strokeLinecap="round" />
    <line x1="19" y1="5" x2="5" y2="19" stroke="#3a0104" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

// ── Main Topbar component ────────────────────────────────────────────────────

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    variant,
    docked,
    solid,
  } = props;

  // Homepage variant: the mobile bar is transparent over the hero (logo +
  // hamburger only) and docks to the solid maroon gradient — with the search
  // icon revealed — once the hero scrolls out of view.
  // Solid mode (`solid`, e.g. SearchPage): the same homepage header pinned in
  // its docked appearance from first paint — no transparent state — and with
  // the flow spacer rendered, since no hero runs underneath it.
  const isHomepageVariant = variant === 'homepage';
  const showDockedAppearance = isHomepageVariant && (docked || solid);

  // ── Mobile menu state ────────────────────────────────────────
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const hamburgerRef = useRef(null);
  const panelRef = useRef(null);

  // ── Mobile search overlay state ──────────────────────────────
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchBtnRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchPanelRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const anyOpen = isMobileMenuOpen || isSearchOpen;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen, isSearchOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handler = e => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isMobileMenuOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isMobileMenuOpen || !panelRef.current) return;
    const panel = panelRef.current;
    const sel =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const els = Array.from(panel.querySelectorAll(sel));
    if (!els.length) return;
    els[0].focus();
    const trap = e => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === els[0]) {
        e.preventDefault();
        els[els.length - 1].focus();
      } else if (!e.shiftKey && document.activeElement === els[els.length - 1]) {
        e.preventDefault();
        els[0].focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [isMobileMenuOpen]);

  // Prevent keyboard focus reaching the offscreen panel when closed
  useEffect(() => {
    if (!panelRef.current) return;
    if (isMobileMenuOpen) {
      panelRef.current.removeAttribute('inert');
    } else {
      panelRef.current.setAttribute('inert', '');
    }
  }, [isMobileMenuOpen, mounted]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setIsSearchOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Search overlay: ESC closes
  useEffect(() => {
    if (!isSearchOpen) return;
    const handler = e => {
      if (e.key === 'Escape') closeSearch();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isSearchOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search overlay: focus trap
  useEffect(() => {
    if (!isSearchOpen || !searchPanelRef.current) return;
    const panel = searchPanelRef.current;
    const sel =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const els = Array.from(panel.querySelectorAll(sel));
    if (!els.length) return;
    const trap = e => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === els[0]) {
        e.preventDefault();
        els[els.length - 1].focus();
      } else if (!e.shiftKey && document.activeElement === els[els.length - 1]) {
        e.preventDefault();
        els[0].focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [isSearchOpen]);

  // Search overlay: inert when closed
  useEffect(() => {
    if (!searchPanelRef.current) return;
    if (isSearchOpen) {
      searchPanelRef.current.removeAttribute('inert');
    } else {
      searchPanelRef.current.setAttribute('inert', '');
    }
  }, [isSearchOpen, mounted]);

  // Search overlay: auto-focus the input on open.
  // TODO: iOS device test keyboard auto-open — useEffect-based focus may not
  // trigger the native keyboard on iOS Safari if React's commit happens outside
  // the tap event loop. If the keyboard doesn't appear on real iOS devices,
  // move the .focus() call synchronously into the search button's onClick
  // handler instead of waiting for isSearchOpen to flip.
  useEffect(() => {
    if (!isSearchOpen) return;
    searchInputRef.current?.focus();
  }, [isSearchOpen]);

  const openMobileMenu = () => setIsMobileMenuOpen(true);
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    hamburgerRef.current?.focus();
  };

  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => {
    setIsSearchOpen(false);
    searchBtnRef.current?.focus();
  };

  // ── Existing handlers ────────────────────────────────────────
  const handleSubmit = values => {
    const {
      currentSearchParams,
      history,
      location,
      config,
      routeConfiguration,
      currentUser,
    } = props;
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    const pushSearch = searchParams =>
      history.push(
        createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
      );

    // Location-based search (non-keyword marketplaces) keeps the original flow.
    if (!isMainSearchTypeKeywords(config)) {
      const { search, selectedPlace } = values?.location || {};
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) ? { origin } : {};
      pushSearch({ ...currentSearchParams, ...originMaybe, address: search, bounds });
      return;
    }

    // Keyword (natural-language) search — Story 8.2 route-at-submit (AC9).
    const rawQuery = (values?.keywords || '').trim();
    if (!rawQuery) {
      // Empty submit clears the keyword search (preserve prior behavior).
      pushSearch({ ...currentSearchParams, keywords: values?.keywords });
      return;
    }
    return routeAndSubmitKeywordSearch({
      rawQuery,
      currentSearchParams,
      currentUser,
      config,
      pushSearch,
    });
  };

  // Overlay search field submit (Enter/go key or magnifier tap) — close the
  // overlay for the navigation, then run the shared keyword path
  // (route-at-submit → record recent). ISOSearchBar passes keywords: '' for an
  // empty submit, which handleSubmit treats as clearing the keyword search.
  const handleOverlaySearchSubmit = values => {
    setIsSearchOpen(false);
    return handleSubmit(values);
  };

  // Overlay prompt card / chip / recent row tapped — run that exact query
  // through the shared route-at-submit orchestration (which also records it
  // to the recents list) and close the overlay for the navigation.
  const handleOverlayQuerySelect = query => {
    setIsSearchOpen(false);
    handleSubmit({ keywords: query });
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';

  const { mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const topbarSearchInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? { search: address, selectedPlace: { address, origin, bounds } }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearchInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';
  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const handleSkipToMainContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      mainContent.addEventListener('blur', () => mainContent.removeAttribute('tabindex'), {
        once: true,
      });
    }
  };

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
      onClose={closeMobileMenu}
    />
  );

  const slideOutPortal = mounted
    ? ReactDOM.createPortal(
        <>
          <div
            className={classNames(css.menuBackdrop, {
              [css.menuBackdropVisible]: isMobileMenuOpen,
            })}
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            id="mobile-nav-panel"
            className={classNames(css.menuPanel, { [css.menuPanelOpen]: isMobileMenuOpen })}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            aria-hidden={!isMobileMenuOpen}
          >
            {authInProgress ? null : mobileMenu}
          </div>
        </>,
        document.body
      )
    : null;

  const searchOverlayPortal = mounted
    ? ReactDOM.createPortal(
        <div
          ref={searchPanelRef}
          id="mobile-search-panel"
          className={classNames(css.searchOverlay, { [css.searchOverlayOpen]: isSearchOpen })}
          role="dialog"
          aria-modal="true"
          aria-label="Search"
          aria-hidden={!isSearchOpen}
        >
          <div className={css.searchOverlayHeader}>
            <button
              type="button"
              className={css.searchOverlayClose}
              onClick={closeSearch}
              aria-label="Close search"
            >
              <CloseIcon />
            </button>
            {/* Same shared search box the mobile homepage hero uses, in its
                compact overlay presentation. */}
            <ISOSearchBar
              variant="overlay"
              onSearchSubmit={handleOverlaySearchSubmit}
              onEmptySubmit={() => handleOverlaySearchSubmit({ keywords: '' })}
              placeholder={HERO_SEARCH_PLACEHOLDER}
              ariaLabel="Search horses"
              inputRef={searchInputRef}
            />
          </div>
          <div className={css.searchOverlayRibbon} aria-hidden="true" />
          <div className={css.searchOverlayBody}>
            <SearchOverlayContent isOpen={isSearchOpen} onQuerySelect={handleOverlayQuerySelect} />
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={classes}>
      <Button onClick={handleSkipToMainContent} className={css.skipToMainContent}>
        <FormattedMessage id="Topbar.skipToMainContent" />
        <IconArrowHead direction="right" size="small" rootClassName={css.skiptoMainArrow} />
      </Button>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />

      {/* Mobile header bar — maroon gradient, logo + search + hamburger.
          Homepage variant: transparent over the hero, docks to maroon on scroll. */}
      <nav
        className={classNames(css.mobileHeader, {
          [css.mobileHeaderHome]: isHomepageVariant,
          [css.mobileHeaderHomeDocked]: showDockedAppearance,
        })}
        aria-label="Mobile navigation"
      >
        <LinkedLogo
          id="logo-mobile-header"
          layout="mobile"
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
          rootClassName={css.mobileLogoWrap}
          logoImageClassName={css.mobileLogoImg}
        />
        <div className={css.mobileHeaderActions}>
          <button
            ref={searchBtnRef}
            className={classNames(css.mobileSearchBtn, {
              // Over the hero the hero search pill sits directly below — the
              // icon only reappears once docked over the listing grid.
              [css.mobileSearchBtnHidden]: isHomepageVariant && !showDockedAppearance,
            })}
            onClick={openSearch}
            aria-label="Open search"
            aria-expanded={isSearchOpen}
            aria-controls="mobile-search-panel"
            type="button"
          >
            <MagnifyingGlassIcon />
          </button>
          <button
            ref={hamburgerRef}
            id={MOBILE_MENU_BUTTON_ID}
            className={css.hamburgerBtn}
            onClick={openMobileMenu}
            aria-label={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-panel"
            type="button"
          >
            {notificationDot}
            <HamburgerIcon />
          </button>
        </div>
      </nav>

      {/* Spacer pushes page content below the fixed mobile header. Skipped on
          the homepage so the hero photo runs behind the transparent bar; solid
          mode renders it (taller — the homepage bar's height). */}
      {!isHomepageVariant || solid ? (
        <div
          className={classNames(css.mobileHeaderSpacer, {
            [css.mobileHeaderSpacerHome]: isHomepageVariant,
          })}
          aria-hidden="true"
        />
      ) : null}

      {/* Desktop header — visible at 768px+ */}
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
          variant={variant}
          docked={docked || solid}
        />
      </div>

      {/* Slide-out mobile menu portal */}
      {slideOutPortal}

      {/* Full-screen mobile search overlay portal */}
      {searchOverlayPortal}

      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_SEARCH_BUTTON_ID}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />
      {/* Story 9.13 — app-wide, cohort-gated onboarding hard gate. Self-connected
          (reads currentUser/auth from the store); engages only for the
          committed-to-selling cohort and suppresses itself while the email-verify
          gate above is active (OD-4). */}
      <OnboardingModal />
      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {Object} props.desktopClassName
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Object} props.location
 * @returns {JSX.Element}
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
